import { useEffect, useState } from 'react'
import { Car, Heart, Flame, Wrench, ShieldCheck, Scale, Boxes, MoreHorizontal, Sprout, X } from 'lucide-react'

type Group = {
  id: string
  icon: React.ReactNode
  title: string
  count: string
  products: string[]
}

const groups: Group[] = [
  {
    id: 'motor',
    icon: <Car size={20} />,
    title: 'Motor',
    count: '13 calculators',
    products: [
      'Private Car',
      'Two-Wheeler',
      'Goods Carrying Vehicle (GCV)',
      'PCV Bus',
      'PCV 2-Wheeler',
      '3-Wheeler GCV',
      '3-Wheeler Passenger',
      'Taxi',
      'Rickshaw',
      'E-Rickshaw',
      'E-Cart',
      'Miscellaneous Vehicles',
      'Commercial PA (CPA)',
    ],
  },
  {
    id: 'health',
    icon: <Heart size={20} />,
    title: 'Health',
    count: '7 plans',
    products: [
      'Family Medicare Policy',
      'Individual Health Policy',
      'Arogya Sanjeevani Policy',
      'Yuvaan Health Insurance',
      'SPECTRA Health Insurance',
      'Samaveshi Suraksha',
      'Uni Criticare Policy',
    ],
  },
  {
    id: 'fire',
    icon: <Flame size={20} />,
    title: 'Fire & Property',
    count: 'Fire, Burglary & more',
    products: ['Fire Insurance', 'Burglary Insurance', 'Hoarding Insurance', 'Neon Sign Insurance'],
  },
  {
    id: 'engineering',
    icon: <Wrench size={20} />,
    title: 'Engineering',
    count: '6 calculators',
    products: [
      'Contractor All Risk (CAR)',
      'Erection All Risk (EAR)',
      'Contractors Plant & Machinery (CPM)',
      'Electronic Equipment Insurance (EEI)',
      'EEI — UIIC GC',
      'Machinery Breakdown (MBD)',
    ],
  },
  {
    id: 'pa',
    icon: <ShieldCheck size={20} />,
    title: 'Personal Accident',
    count: '5 plans',
    products: [
      'Individual Personal Accident',
      'Saral Suraksha Bima',
      'Sampoorna Suraksha Bima',
      'Group Marga Bandhu',
      'Students Safety Insurance',
    ],
  },
  {
    id: 'liability',
    icon: <Scale size={20} />,
    title: 'Liability',
    count: '5+ products',
    products: [
      'Professional Indemnity (Doctors)',
      'Carriers Legal Liability',
      'Workmen’s Compensation',
      'Cyber Kavach',
      'Fidelity Guarantee',
    ],
  },
  {
    id: 'package',
    icon: <Boxes size={20} />,
    title: 'Package Policies',
    count: '6+ packages',
    products: [
      'Shopkeeper Policy',
      'Jewellers Block',
      'Compact Policy',
      'Householder Policy',
      'Wedding Bells Policy',
      'LPG Traders Policy',
    ],
  },
  {
    id: 'misc',
    icon: <MoreHorizontal size={20} />,
    title: 'Miscellaneous',
    count: 'TV, Money, Glass, Hoarding…',
    products: [
      'TV Insurance',
      'Money Insurance',
      'Plate Glass Insurance',
      'Hoarding Insurance',
      'Neon Sign Insurance',
      'Burglary Insurance',
    ],
  },
  {
    id: 'social',
    icon: <Sprout size={20} />,
    title: 'Social & Rural',
    count: 'For rural communities',
    products: [
      'Cattle / Livestock Insurance',
      'Janata Personal Accident',
      'Rural Package Policy',
      'Senior Citizen Red Carpet',
    ],
  },
]

export function ProductsCovered() {
  const [openId, setOpenId] = useState<string | null>(null)
  const active = groups.find((g) => g.id === openId)

  useEffect(() => {
    if (!openId) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpenId(null)
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', onKey)
    }
  }, [openId])

  return (
    <section id="products" className="bg-white">
      <div className="mx-auto max-w-6xl px-5 py-16 md:py-20">
        <div className="max-w-2xl">
          <span className="chip bg-brand/10 text-brand">Coverage</span>
          <h2 className="mt-3">What you can quote</h2>
          <p className="mt-3 text-ink-soft">
            Eight lines of business. Fifty-plus calculators. Tap a chip to see
            what’s inside.
          </p>
        </div>

        <div className="mt-8 grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {groups.map((g) => (
            <button
              key={g.id}
              onClick={() => setOpenId(g.id)}
              className="group flex items-center gap-4 rounded-card border border-[var(--color-divider)] bg-white p-5 text-left shadow-card transition-transform hover:-translate-y-0.5 hover:border-brand/40"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand/10 text-brand">
                {g.icon}
              </span>
              <span className="flex-1">
                <span className="block text-base font-semibold">{g.title}</span>
                <span className="block text-xs text-ink-soft">{g.count}</span>
              </span>
              <span className="text-xs font-semibold text-brand opacity-0 transition-opacity group-hover:opacity-100">
                View
              </span>
            </button>
          ))}
        </div>
      </div>

      {active && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-3 sm:items-center"
          onClick={() => setOpenId(null)}
        >
          <div
            className="w-full max-w-lg rounded-modal bg-white p-6 shadow-card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={active.title}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand/10 text-brand">
                  {active.icon}
                </div>
                <h3 className="mt-3">{active.title}</h3>
                <p className="text-sm text-ink-soft">{active.count}</p>
              </div>
              <button
                aria-label="Close"
                onClick={() => setOpenId(null)}
                className="rounded-pill p-2 text-ink-soft hover:bg-black/5"
              >
                <X size={20} />
              </button>
            </div>
            <ul className="mt-4 grid gap-2 sm:grid-cols-2">
              {active.products.map((p) => (
                <li
                  key={p}
                  className="rounded-input border border-[var(--color-divider)] bg-[var(--color-nav)] px-3 py-2 text-sm"
                >
                  {p}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs text-ink-soft">
              List is for reference. Calculation happens inside the app.
            </p>
          </div>
        </div>
      )}
    </section>
  )
}
